/**
 * Живой ответ движка — единственная тёмная поверхность кабинета.
 *
 * Ради неё тёмное в кабинете вообще разрешено (foundations.md): здесь показан
 * не показатель, а сам ответ, который движок выдал покупателю. Цитата стоит на
 * двух экранах — на обзоре бренда и в разрезе по движкам, — и стоит одной
 * реализацией: разъехавшиеся копии означали бы, что счётчик «Ответ 3 из 214»
 * считает на двух экранах разное, хотя данные одни.
 *
 * Выборка ответов объявлена здесь же, `useAnswers`. Второй набор параметров
 * запроса на соседнем экране — это ровно тот способ, которым «из 214» на
 * обзоре превращается в «из 20» на движках без единой правки в цифрах.
 */

import { useState } from 'react';

import { useRead, type Page, type Read } from './query';
import { engineTitle } from './controls';
import { dateNumeric } from '@shared/src/format';
import type { components } from '../api/schema';

export type Answer = components['schemas']['Answer'];

/**
 * Сколько ответов берём за раз.
 *
 * Столько же берёт матрица «вопрос × движок»: одна выборка обслуживает и её, и
 * цитату, и второй запрос за теми же ответами экран не делает.
 */
const ANSWERS_LIMIT = 500;

/** Ответы движков по бренду — одни и те же на всех экранах кабинета. */
export function useAnswers(brandId: string): Read<Page<Answer>> {
  return useRead<Page<Answer>>(`/brands/${brandId}/answers`, {
    limit: ANSWERS_LIMIT,
    organic: true,
  });
}

/**
 * Цитата из ответа движка с переключением по одному.
 *
 * Пока ответов нет (грузятся, не пришли, все без текста) — блока нет вовсе:
 * пустая тёмная плашка на светлом экране читается как поломка.
 *
 * `brandId` здесь не для запроса, а для тождества показанного. Обзор при смене
 * бренда экран не размонтирует, и без этого номер долистанного ответа
 * переезжал на другой бренд: пролистав до 200-го ответа и переключившись на
 * бренд с двенадцатью, человек открывал его сразу на «Ответ 12 из 12».
 * Состояние вычисляется при отрисовке, а не выправляется эффектом, — тот же
 * приём, что в `useRead`: иначе кадр показывал бы чужую цитату.
 */
export function LiveAnswer({
  brandId,
  answers,
  titles,
}: {
  brandId: string;
  answers: Read<Page<Answer>>;
  titles: Map<string, string>;
}) {
  const items = answers.kind === 'ready' ? answers.data.items : [];
  const withText = items.filter((answer) => answer.snippet);
  const [held, setHeld] = useState({ of: brandId, at: 0 });
  // Список того же бренда может прийти короче (перезагрузка после нового
  // замера) — тогда остаёмся в его границах, а не улетаем в пустоту.
  const at =
    held.of === brandId ? Math.min(held.at, Math.max(0, withText.length - 1)) : 0;
  const answer = withText[at];

  if (!answer) return null;

  return (
    <section className="block">
      <h2>Что именно ответил движок</h2>
      <figure className="inst">
        <div className="inst-top">
          <span className="inst-tag">
            {engineTitle(titles, answer.engine_code)} · {dateNumeric(answer.fetched_at)}
            {answer.model_version ? ` · ${answer.model_version}` : ''}
          </span>
          <span className="stamp on-dark">
            {answer.brand_mentioned
              ? answer.brand_position
                ? `Назван, место ${answer.brand_position}`
                : 'Назван'
              : 'Не назван'}
          </span>
        </div>

        <p className="inst-q">
          {/* Каретка прибора — Lucide, а не глиф: `→` в основной гарнитуре
              приходит из системного запасного шрифта и сидит мимо базовой
              линии. Стрелки-знаки разрешены только внутри моно-подписей
              (foundations.md). */}
          <svg
            className="caret"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M9 6l6 6-6 6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {answer.question_text}
        </p>
        <p className="inst-a">{answer.snippet}</p>

        <figcaption className="inst-foot">
          {withText.length > 1 ? `Ответ ${at + 1} из ${withText.length}` : ''}
        </figcaption>

        {withText.length > 1 ? (
          <p className="quote-src">
            <button
              className="btn btn-ondark btn-sm"
              type="button"
              // Шаг считается от ПРЕДЫДУЩЕГО состояния, а не от отрисованного
              // номера: два быстрых нажатия попадают в одну пачку React, и с
              // подстановкой готового числа второе нажатие повторяло первое —
              // кнопка «залипала» на соседнем ответе.
              onClick={() =>
                setHeld((current) => {
                  const from =
                    current.of === brandId
                      ? Math.min(current.at, withText.length - 1)
                      : 0;
                  return { of: brandId, at: (from + 1) % withText.length };
                })
              }
            >
              Следующий ответ
            </button>
          </p>
        ) : null}
      </figure>
    </section>
  );
}
